import * as React from "react";
import {Button, ButtonProps} from "@bizform/primereact/button";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {DomAttribute} from "@bizform/type/model/dom/DomAttribute.model";
import {ModalCloseOptionKeyConstant} from "@bizform/type/model/component_element/manifest_constant/Modal.constant";
import {
    GridActionKeyConstant,
    GridAutoCloseKeyConstant,
    GridConfirmKeyConstant,
    GridIdKeyConstant,
    GridNoItemKeyConstant,
    GridOnlyOneKeyConstant,
    GridSuccessKeyConstant,
    GridValidationKeyConstant
} from "@bizform/type/model/component_element/manifest_constant/Grid.constant";
import {FormStateKey} from "@bizform/type/model/form/FormStateKey.model";
import {ComponentHelper, Debug} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    ComponentElementClass,
    FormStateService,
    GridActionService,
    ModalHandler,
    ReactComponentElementInit
} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


type Props = ButtonProps & {
};

type WrappedCompState = {
    label?: string;
    goToForm?: string;
    icon?: string;
    iconPos?: string;
    cornerStyleClass?: string;
    style?: object;
    modalCloseOption?: string;
    role?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    disabled?: boolean;
    [GridActionKeyConstant]?: string;
    [GridValidationKeyConstant]?: boolean;
    autoReload?: boolean;
    onClick?: (e) => void;
};

type WrappedFormState = {
    onClick?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * Button component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfButton extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_lxwVGoz0KufIzW6vti8f1GUv0vNT2eq",
        category: ComponentCategory.BUTTON,
        label: I18n.componentElement.button.label,
        description: I18n.componentElement.button.description,
        icon: "",
        property: null
    };

    focus(): void {
        if (this._button) {
            this._button.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            if (!newState.wrappedCompState.role) {
                delete(newState.wrappedCompState.role);
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emitFormState(value: string, e?: MouseEvent): void {
    }

    @ReactComponentElementInit(BfButton)
    static init(): ComponentElement<null> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _button: HTMLInputElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                label: null,
                icon: null,
                iconPos: "left"
            },
            wrappedFormState: {
                onClick: null
            }
        };

        this._setRef = this._setRef.bind(this);
        this._onClickEvent = this._onClickEvent.bind(this);
        this._onClick = this._onClick.bind(this);
    }


    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    componentWillUnmount(): void {
        this._button = null;
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            /*
             * Button doesn't have readOnly property
             */
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        let modalCloseOption;
        if (typeof newProps.modalCloseOption === "string") {
            modalCloseOption = {
                [newProps.modalCloseOption]: ""
            };
        }
        if (this.state.wrappedCompState.gridAction) {
            if (!modalCloseOption) {
                modalCloseOption = {};
            }
            modalCloseOption[DomAttribute.GRIDBUTTON] = this.state.wrappedCompState.autoReload ? "reload" : "";
        }
        delete newProps.goToForm;
        delete newProps.attribute;
        delete newProps.duplicated;
        delete newProps.autoReload;
        delete newProps[ModalCloseOptionKeyConstant];
        delete newProps[GridIdKeyConstant];
        delete newProps[GridActionKeyConstant];
        delete newProps[GridValidationKeyConstant];
        delete newProps[GridAutoCloseKeyConstant];
        delete newProps[GridConfirmKeyConstant];
        delete newProps[GridSuccessKeyConstant];
        delete newProps[GridNoItemKeyConstant];
        delete newProps[GridOnlyOneKeyConstant];
        delete newProps.onClick;
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        return (
            <Button
                {...newProps}
                {...modalCloseOption}
                id={this.elementId}
                key={key}
                ref={this._setRef}
                style={{ width: "100%" , ...this.state.wrappedCompState.style}}
                onClick={this._onClick}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };
        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _setRef(ref) {
        if (ref) {
            this._button = ref.element;
            // if (!this.state.wrappedCompState.label) {
            //     const text = this._button.querySelector(".p-button-text");
            //     if (text) {
            //         text.parentElement.removeChild(text);
            //     }
            // }
            this.readyComponent();
        }
    }

    private _onClick(e: any) {
        if ((this as any).___props.data.component === "_c3dIwW__cKOc66T6VV3EQgeRNE4Nz1s" || (this as any).___props.data.component === "_ywGI0oucChedsEeTOcbr1FXNIuR_A3h") {
            if (this.state.wrappedFormState.onClick && this.state.wrappedFormState.onClick.indexOf(`"EXIT"`) > -1) {
                const state = FormStateService.getFormStateDataByKeys(this.applicationId, this.formId, [FormStateKey.FORMACTION, FormStateKey.WIHACTION])["state"];
                if (state[FormStateKey.FORMACTION] === "EXIT" || state[FormStateKey.WIHACTION] === "EXIT") {
                    FormStateService.removeStateDataKeys(this.applicationId, this.formId, null, [FormStateKey.FORMACTION, FormStateKey.WIHACTION]);
                }
            }
        }
        this.state.wrappedCompState.onClick(e);
    }

    protected _onClickEvent(e: any): void {
        if (this._button) {
            if (this.state.wrappedCompState.gridAction) {
                GridActionService.callAction(this.applicationId, this.formId, this.state.wrappedCompState[GridActionKeyConstant], this._button, (this as any).___props);
            } else if (this._button.hasAttribute(DomAttribute.MODALBUTTONCLOSE)) {
                ModalHandler.clickOnlyCloseButton(this._button as HTMLElement);
            } else if (this._button.hasAttribute(DomAttribute.MODALBUTTONAPPLY) && this.state.wrappedCompState[GridValidationKeyConstant] !== true && this.state.wrappedCompState[GridValidationKeyConstant] !== false) {
                ModalHandler.clickApplyButton(this._button as HTMLElement);
            }
        }
    }
}
